import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import AmbassadorShell, { card, cream, eyebrow, ghostBtn, gold, h1Style, ink, line, muted, primaryBtn, Reveal } from "./_AmbassadorShell";
import { EvaVoiceTap } from "@/components/landing/EvaVoiceTap";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useAuth } from "@/hooks/useAuth";

export default function Ambassadors() {
  const [perMonth, setPerMonth] = useState(2);
  const [months, setMonths] = useState<3 | 6 | 12 | 24>(12);
  const showHeroVideo = useMediaQuery("(min-width: 641px) and (prefers-reduced-motion: no-preference)");
  const { user } = useAuth();
  const applyHref = user ? "/ambassadors/dashboard" : "/ambassadors/apply";
  const applyLabelHero = user ? "Go to your dashboard →" : "Apply to be an Insider →";
  const applyLabelFinal = user ? "Open your dashboard →" : "Apply now →";

  const sim = useMemo(() => simulateCircle({ perMonth, months }), [perMonth, months]);

  return (
    <AmbassadorShell wide>
      <Helmet>
        <title>Eva Insiders — Ambassador Program | HireEva</title>
        <meta name="description" content="Refer small businesses to Eva and earn cash commissions. Build a Circle of operators learning AI together. Apply to the Eva Insiders program." />
        <link rel="canonical" href="https://hireeva.co/ambassadors" />
        <meta property="og:title" content="Eva Insiders — Ambassador Program" />
        <meta property="og:description" content="Refer small businesses to Eva and earn cash commissions. Build a Circle of operators learning AI together." />
        <meta property="og:url" content="https://hireeva.co/ambassadors" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        })}</script>
      </Helmet>
      <style>{`
        .eva-amb-focus:focus-visible { outline: 2px solid #C4823A; outline-offset: 2px; border-radius: 8px; }
        @media (max-width: 640px) {
          .eva-amb-hero-cta { width: 100%; flex-direction: column; align-items: stretch; }
          .eva-amb-hero-cta > a { width: 100%; text-align: center; justify-content: center; }
        }
        details.eva-amb-rate-card[open] > summary .eva-amb-chevron { transform: rotate(180deg); }
        .eva-amb-chevron { display: inline-block; transition: transform 200ms ease; }
        .eva-amb-faq-toggle { margin-left: auto; font-family: 'Instrument Serif', serif; font-style: italic; font-size: 22px; line-height: 1; color: #C4823A; transition: transform 200ms ease; }
        details[open] > summary .eva-amb-faq-toggle { transform: rotate(45deg); }
        @media (prefers-reduced-motion: reduce) {
          .eva-amb-pulse-dot { animation: none !important; }
        }
      `}</style>
      {/* AMBIENT STARFIELD — pure CSS, no video. Soft blooms + tiny pinpoints over cream. */}
      {showHeroVideo && (
        <>
          {/* Layer 1: large soft blooms — gives the diffuse "warm haze" feeling */}
          <div aria-hidden style={{
            position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none",
            background: [
              "radial-gradient(circle at 12% 18%, rgba(255,236,200,0.18), transparent 28%)",
              "radial-gradient(circle at 88% 32%, rgba(255,228,196,0.14), transparent 32%)",
              "radial-gradient(circle at 22% 78%, rgba(255,240,210,0.12), transparent 30%)",
              "radial-gradient(circle at 78% 88%, rgba(255,232,200,0.10), transparent 28%)",
            ].join(", "),
          }} />
          {/* Layer 2: tiny star pinpoints — low opacity, multiple sizes */}
          <div aria-hidden style={{
            position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none",
            opacity: 0.45,
            backgroundImage: [
              // tight pinpoints
              "radial-gradient(1px 1px at 23px 47px, rgba(120,90,40,0.55), transparent 60%)",
              "radial-gradient(1px 1px at 137px 211px, rgba(120,90,40,0.45), transparent 60%)",
              "radial-gradient(1px 1px at 289px 89px, rgba(120,90,40,0.50), transparent 60%)",
              "radial-gradient(1px 1px at 71px 311px, rgba(120,90,40,0.40), transparent 60%)",
              "radial-gradient(1px 1px at 401px 167px, rgba(120,90,40,0.50), transparent 60%)",
              "radial-gradient(1px 1px at 233px 379px, rgba(120,90,40,0.45), transparent 60%)",
              // slightly larger glints
              "radial-gradient(1.5px 1.5px at 359px 271px, rgba(196,130,58,0.40), transparent 65%)",
              "radial-gradient(1.5px 1.5px at 113px 143px, rgba(196,130,58,0.35), transparent 65%)",
              "radial-gradient(1.5px 1.5px at 461px 421px, rgba(196,130,58,0.35), transparent 65%)",
            ].join(", "),
            backgroundSize: "520px 520px",
            backgroundRepeat: "repeat",
          }} />
          {/* Layer 3: cream radial mask — keeps the reading column the cleanest */}
          <div aria-hidden style={{
            position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none",
            background: "radial-gradient(ellipse 70% 60% at center, rgba(248,244,236,0.78) 0%, rgba(248,244,236,0.45) 55%, rgba(248,244,236,0.15) 100%)",
          }} />
        </>
      )}

      {/* HERO */}
      <Reveal as="section">
        <section style={{
          position: "relative",
          overflow: "hidden",
          paddingTop: "clamp(48px, 8vw, 80px)",
          paddingBottom: "clamp(48px, 8vw, 80px)",
          marginTop: -60,
          marginLeft: "calc(50% - 50vw)",
          marginRight: "calc(50% - 50vw)",
          paddingLeft: "max(24px, calc(50vw - 588px))",
          paddingRight: "max(24px, calc(50vw - 588px))",
        }}>
          {/* Hero ambient: amber bloom on the right reinforces the starfield without coins. */}
          {showHeroVideo && (
            <div aria-hidden style={{
              position: "absolute", inset: 0, zIndex: 0, pointerEvents: "none",
              background: [
                "radial-gradient(ellipse 60% 70% at 85% 40%, rgba(196,130,58,0.18), transparent 60%)",
                "radial-gradient(ellipse 40% 50% at 75% 70%, rgba(255,228,196,0.14), transparent 65%)",
                `linear-gradient(180deg, transparent 0%, transparent 70%, ${cream} 100%)`,
              ].join(", "),
            }} />
          )}
          <div style={{ position: "relative", zIndex: 1 }}>
            <p style={eyebrow}>
              <span aria-hidden style={{ display: "inline-block", width: 6, height: 6, borderRadius: 999, background: "#C4823A", boxShadow: "0 0 8px #C4823A", marginRight: 8, verticalAlign: "middle" }} />
              A note from Eva
            </p>
            <h1 style={h1Style}>Build a Circle. <em>I'll pay you</em><br/>for every business you bring in.</h1>
            <p style={{ fontSize: 19, color: muted, maxWidth: 680, marginBottom: 14, fontStyle: "italic" }}>
              The big AI labs are building for the Fortune 500. They will quietly put a million small businesses out of work, and they will not lose sleep over it.
            </p>
            <p style={{ fontSize: 17, color: muted, maxWidth: 680, marginBottom: 16, lineHeight: 1.6 }}>
              I was built for the other side of that line — SMB's, independents, operators, family shops. Bring me one of them. I'll pay you 10% of what they pay in their first three months. But the money is not the point. The Circle is. It is the future of small business in America and the world..
            </p>
            <div className="eva-amb-hero-cta" style={{ marginTop: 32, display: "flex", gap: 12, flexWrap: "wrap" }}>
              <Link to={applyHref} style={primaryBtn}>{applyLabelHero}</Link>
              <Link to="/ambassadors/terms" style={ghostBtn}>Read the program terms</Link>
            </div>
            <p style={{ marginTop: 14, fontSize: 13, color: muted, letterSpacing: "0.02em" }}>
              <span aria-hidden style={{ display: "inline-block", width: 5, height: 5, borderRadius: 999, background: "#C4823A", marginRight: 8, verticalAlign: "middle" }} />
              No résumé. Two minutes.
            </p>
          </div>
        </section>
      </Reveal>

      {/* WHY THIS EXISTS */}
      <Section title="Why this exists" eyebrow="The thesis">
        <div style={{ ...card, background: "#FBF8F1", borderColor: line }}>
          <p style={{ margin: "0 0 14px", fontFamily: "'Instrument Serif', serif", fontStyle: "italic", fontSize: 22, lineHeight: 1.4, color: ink }}>
            The commissions are real. The Circle is the point.
          </p>
          <p style={{ margin: 0, color: muted, fontSize: 16, lineHeight: 1.7 }}>
            I pay you for every business you bring in, straight into your bank account. That part's table stakes. The part that actually matters is the room you build around it — operators who know each other, share what's working, and have somewhere to go when something breaks. Comradery. That's the whole game.
          </p>
        </div>

        {/* HOW TO RUN A CIRCLE — collapsible preview, links to full playbook */}
        <details className="eva-amb-rate-card" style={{ ...card, marginTop: 16, cursor: "pointer", borderColor: "rgba(196,130,58,0.35)" }}>
          <summary className="eva-amb-focus" style={{ listStyle: "none", display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ ...eyebrow, color: "#8a5a22", margin: 0, display: "block", marginBottom: 4 }}>How to actually run one</span>
              <span style={{ fontSize: 16, color: ink, fontWeight: 600 }}>
                Not a $10K mastermind. Five owners, a recurring call, half AI and half business.
              </span>
            </span>
            <span aria-hidden className="eva-amb-chevron" style={{ color: muted, fontSize: 18 }}>▾</span>
          </summary>
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${line}` }}>
            <p style={{ margin: "0 0 14px", color: muted, fontSize: 15, lineHeight: 1.7 }}>
              A Circle is 5–15 small-business owners on a recurring video call — weekly or biweekly, 60 minutes, cameras on. Half AI (what you tried, what worked), half business (what's stuck, what you need help with). The four moves to start one:
            </p>
            <ul style={{ margin: "0 0 16px", paddingLeft: 22, color: muted, fontSize: 15, lineHeight: 1.85 }}>
              <li><strong style={{ color: ink }}>Pick your people</strong> — owners running 10–50 person businesses, complementary not competitive.</li>
              <li><strong style={{ color: ink }}>Set the cadence</strong> — same day, same time, locked for 90 days.</li>
              <li><strong style={{ color: ink }}>Pick a format</strong> — Workshop, Hot Seat, or Stand-up. Pick one, run it three times.</li>
              <li><strong style={{ color: ink }}>Run the first one</strong> — intro round, one win, one stuck, one ask. Done in 60.</li>
            </ul>
            <Link to="/ambassadors/playbook" style={primaryBtn}>Read the full playbook →</Link>
          </div>
        </details>
      </Section>

      {/* HOW IT WORKS */}
      <Section title="How it works" eyebrow="Three steps">
        <div style={grid3}>
          {[
            { n: "01", h: "Tell me who you are", b: "Two minutes, one form. Tell me who you are and who you serve." },
            { n: "02", h: "Bring people who need backup", b: "Independents. Operators. Anyone the big platforms forgot. Your link tags every signup back to you — no codes for buyers to remember." },
            { n: "03", h: "Get paid while we build the Circle", b: "Cash to your bank on the 1st of the month: 10% of what each new customer pays in their first three months." },
          ].map((s) => (
            <div key={s.n} className="eva-amb-card-hover" style={card}>
              <div style={{ ...eyebrow, marginBottom: 8 }}>Step {s.n}</div>
              <h3 style={{ fontSize: 22, margin: "0 0 8px", fontFamily: "'Instrument Serif', serif", fontStyle: "italic", fontWeight: 400 }}>{s.h}</h3>
              <p style={{ color: muted, margin: 0 }}>{s.b}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* EARNINGS CALCULATOR */}
      <Section title="What could you earn?" eyebrow="Quick math · this is your business now">
        <p style={{ color: muted, marginTop: -12, marginBottom: 18, maxWidth: 720, fontStyle: "italic" }}>
          Adjust the dials. The math is honest.
        </p>
        <div style={{
          maxWidth: 720,
          margin: "0 0 22px",
          padding: "14px 18px",
          borderLeft: `3px solid #C4823A`,
          background: "rgba(196,130,58,0.06)",
          borderRadius: "0 10px 10px 0",
          fontFamily: "'Instrument Serif', serif",
          fontStyle: "italic",
          fontSize: 18,
          lineHeight: 1.45,
          color: ink,
        }}>
          "Word-of-mouth is the only marketing that's ever worked. So I pay for it directly."
          <span style={{ display: "block", marginTop: 6, fontFamily: "Barlow, sans-serif", fontStyle: "normal", fontSize: 12, letterSpacing: "0.08em", textTransform: "uppercase", color: muted }}>— the founder</span>
        </div>
        <div style={{ ...card }}>
          {/* INPUTS */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 24, alignItems: "start" }}>
            <div>
              <label style={{ ...eyebrow, color: muted }}>I can realistically bring in</label>
              <input
                type="range"
                aria-label="People you bring in per month"
                aria-valuetext={`${perMonth} ${perMonth === 1 ? "person" : "people"} per month`}
                min={1}
                max={25}
                value={perMonth}
                onChange={(e) => setPerMonth(Number(e.target.value))}
                className="eva-amb-focus"
                style={{ width: "100%", margin: "8px 0", accentColor: "#C4823A" }}
              />
              <div style={{ fontSize: 28, fontFamily: "'Instrument Serif', serif", fontStyle: "italic", lineHeight: 1.1 }}>
                {perMonth} {perMonth === 1 ? "person" : "people"} <span style={{ color: muted, fontSize: 16 }}>/ month</span>
              </div>
            </div>
            <div>
              <label style={{ ...eyebrow, color: muted }}>For how long</label>
              <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                {([3, 6, 12, 24] as const).map((m) => (
                  <button key={m} onClick={() => setMonths(m)} className="eva-amb-focus" style={{
                    padding: "8px 12px", borderRadius: 8,
                    border: `1px solid ${months === m ? ink : line}`,
                    background: months === m ? ink : "#fff",
                    color: months === m ? "#fff" : ink,
                    cursor: "pointer", fontSize: 13, fontFamily: "inherit",
                  }}>{m} mo</button>
                ))}
              </div>
            </div>
          </div>

          <hr style={{ border: 0, borderTop: `1px solid ${line}`, margin: "24px 0" }} />

          {/* SPARKLINE */}
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
              <div style={{ ...eyebrow, color: muted }}>Monthly earnings, month by month</div>
              <div style={{ fontSize: 12, color: muted, display: "inline-flex", alignItems: "baseline", gap: 6 }}>
                Peak:
                <strong style={{
                  color: "#8a5a22",
                  fontFamily: "'Instrument Serif', serif",
                  fontStyle: "italic",
                  fontSize: 16,
                  display: "inline-block",
                  minWidth: 84,
                  textAlign: "right",
                  fontVariantNumeric: "tabular-nums",
                }}>${sim.peakMonthly.toLocaleString()}</strong>
              </div>
            </div>
            <Sparkline values={sim.monthly} />
            <div style={{ display: "flex", justifyContent: "space-between", color: muted, fontSize: 11, marginTop: 4 }}>
              <span>Month 1</span>
              <span>Month {months}</span>
            </div>
          </div>

          {/* READOUT */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 24, marginTop: 24 }}>
            <div style={{ display: "grid", gap: 12 }}>
              <Stat label="Peak monthly income" value={`$${sim.peakMonthly.toLocaleString()}/mo`} />
              <Stat label={`Total over ${months} months`} value={`$${sim.totalPeriod.toLocaleString()}`} accent />
              <Stat label="Per customer you refer" value={`$${PER_CUSTOMER.toLocaleString()}`} />
            </div>
            <div>
              <div style={{ ...eyebrow, color: muted, marginBottom: 10 }}>What your peak month covers</div>
              <div style={{ display: "grid", gap: 6 }}>
                {MILESTONES.map((m) => (
                  <Milestone key={m.amount} milestone={m} unlocked={sim.peakMonthly >= m.amount} />
                ))}
              </div>
            </div>
          </div>

          <p style={{ color: ink, fontSize: 14, margin: "22px 0 6px", fontStyle: "italic", lineHeight: 1.55 }}>
            I'd rather pay you than pay Facebook. The people who actually use me, who know other operators, who'll vouch at the bar — that's the best marketing money can buy. So I buy it from you, for every business you bring in.
          </p>
          <p style={{ color: muted, fontSize: 13, margin: "10px 0 0" }}>
            Assumes Build Velocity 1 ($1,500/mo), paid monthly: 10% of each of their first three payments, $450 per customer. <strong style={{ color: ink }}>Actual earnings depend on plan mix and refunds.</strong>
          </p>

          {/* COLLAPSIBLE: full commission schedule + assumptions */}
          <details className="eva-amb-rate-card" style={{ marginTop: 16, borderTop: `1px solid ${line}`, paddingTop: 16 }}>
            <summary className="eva-amb-focus" style={{ cursor: "pointer", listStyle: "none", display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: ink, fontWeight: 600 }}>
              <span style={{ ...eyebrow, color: muted, margin: 0 }}>See the rate card & assumptions</span>
              <span aria-hidden className="eva-amb-chevron" style={{ color: muted }}>▾</span>
            </summary>
            <div style={{ display: "grid", gap: 16, marginTop: 16 }}>
              <div style={{ background: "#FBF8F1", padding: "12px 16px", borderRadius: 8, fontSize: 14 }}>
                <strong>Cash. Paid on the 1st of every month.</strong> <span style={{ color: muted }}>No points, no credits, no funny stuff.</span>
              </div>
              <Row title="Every new customer you refer" left="10% of what they pay in their first three months" right="About $450 on a $1,500/mo plan, paid as they pay" />
              <p style={{ color: muted, fontSize: 13, margin: 0 }}>
                Subscription fees only, after discounts and before tax. Anything billed separately, like projects, extra capacity packs or audits, doesn't count. If they pay the first three months up front, you get 10% of that payment, once. New customers only, referred by you directly.
              </p>
              <p style={{ color: muted, fontSize: 13, margin: 0 }}>
                Each commission clears a 30-day hold after the customer pays, then goes out on the 1st. Refunds, chargebacks and disputes reverse the matching commission. $50 minimum payout; smaller balances roll forward. <Link to="/ambassadors/terms" style={{ color: ink }}>Full terms →</Link>
              </p>
            </div>
          </details>
        </div>
      </Section>

      {/* CIRCLES */}
      <Section title="Your Circle, your people" eyebrow="The room you're building">
        <p style={{ color: muted, marginBottom: 24, maxWidth: 720 }}>
          Every Insider has an <em style={{ fontFamily: "'Instrument Serif', serif" }}>Inner Circle</em> — the people you brought in. As your Circle grows, you unlock recurring rooms where your people meet each other, share what's working, and the founder drops in. You run the room. I just show up.
          {" "}Not sure how to actually run one? <Link to="/ambassadors/playbook" style={{ color: ink, textDecoration: "underline", textUnderlineOffset: 3 }}>Here's the playbook →</Link>
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16, marginBottom: 24 }}>
          <Tier badge="5+" title="Monthly founder drop-in" body="You host your Circle. The founder drops in for 30 minutes once a month — live Q&A, strategy, what's moving in AI right now." />
          <Tier badge="25+" title="Weekly founder drop-in" body="You host your Circle. The founder drops in for a full hour every week. Schedule it from your dashboard." featured />
        </div>

        {/* FOUNDING INSIDERS — limited-time race rewards (bonus on top of the standing tiers) */}
        <div style={{
          position: "relative",
          marginBottom: 24,
          padding: "20px 22px",
          borderRadius: 14,
          border: `1.5px solid #C4823A`,
          background: "linear-gradient(135deg, rgba(196,130,58,0.10) 0%, rgba(196,130,58,0.04) 60%, transparent 100%)",
          overflow: "hidden",
        }}>
          <div aria-hidden style={{
            position: "absolute", top: 0, right: 0, width: 240, height: 240,
            background: "radial-gradient(circle at 70% 30%, rgba(196,130,58,0.18), transparent 60%)",
            pointerEvents: "none",
          }} />
          <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <span aria-hidden className="eva-amb-pulse-dot" style={{
              display: "inline-block", width: 8, height: 8, borderRadius: 999,
              background: "#C4823A", boxShadow: "0 0 12px #C4823A",
              animation: "evaPulseDot 1.8s ease-in-out infinite",
            }} />
            <span style={{ ...eyebrow, color: "#8a5a22", margin: 0 }}>Founding Insiders · limited</span>
          </div>
          <h3 style={{
            position: "relative",
            margin: "0 0 10px",
            fontFamily: "'Instrument Serif', serif",
            fontStyle: "italic",
            fontSize: 24,
            lineHeight: 1.2,
            fontWeight: 400,
            color: ink,
          }}>
            First in. Most rewarded.
          </h3>
          <p style={{ position: "relative", color: muted, fontSize: 14, margin: "0 0 14px", maxWidth: 640, lineHeight: 1.55 }}>
            We're seeding the Circle with the people who show up first. Hit these milestones early and the founder unlocks more time with your room — on the house, for three months.
          </p>
          <div style={{ position: "relative", display: "grid", gap: 10 }}>
            <FoundingRow
              rank="First 5 Insiders to 5 referrals"
              reward="Weekly 30-min founder drop-in to your Circle · 3 months"
            />
            <FoundingRow
              rank="First Insider to 10 referrals"
              reward="Weekly 1-hour founder drop-in to your Circle · 3 months"
              featured
            />
          </div>
          <p style={{ position: "relative", color: muted, fontSize: 12, margin: "14px 0 0", fontStyle: "italic" }}>
            Tracked from program launch · race milestones, separate from your standing tier. We'll announce winners in your dashboard the day they qualify.
          </p>
        </div>

        <p style={{ color: muted, fontSize: 13, marginTop: 16 }}>
          You see who's in your Inner Circle, can email them once per scheduled event, and pick a slot from the founder's published availability. Minimum 5 RSVPs for the founder to attend — if fewer, you keep the room on your own. That's the point.
        </p>
      </Section>

      {/* FAQ */}
      <Section title="Quick questions" eyebrow="FAQ">
        <div style={{ display: "grid", gap: 14 }}>
          {FAQS.map((f) => (
            <details key={f.q} className="eva-amb-card-hover" style={{ ...card, cursor: "pointer" }}>
              <summary className="eva-amb-focus" style={{ fontWeight: 600, fontSize: 16, listStyle: "none", display: "flex", alignItems: "center", gap: 12 }}>
                <span style={{ flex: 1, minWidth: 0 }}>
                  {f.q}
                </span>
                <span aria-hidden className="eva-amb-faq-toggle">+</span>
              </summary>
              <p style={{ color: muted, margin: "10px 0 0" }}>{f.a}</p>
              {(f.q.startsWith("How do I actually run") || f.q.startsWith("Do I need to know how to run") || f.q.startsWith("What do you mean by")) && (
                <p style={{ margin: "10px 0 0" }}>
                  <Link to="/ambassadors/playbook" style={{ color: gold, fontWeight: 600, textDecoration: "underline", textUnderlineOffset: 3 }}>
                    Read the Circle Runbook →
                  </Link>
                </p>
              )}
              {f.q.startsWith("What do you mean by") && (
                <div style={{ marginTop: 10 }}>
                  <EvaVoiceTap clipId="insider_circle" hint="what's a Circle?" />
                </div>
              )}
            </details>
          ))}
        </div>
      </Section>

      {/* CTA */}
      <section style={{ marginTop: 80, padding: "56px 32px", background: ink, color: "#F8F4EC", borderRadius: 16, textAlign: "center", position: "relative", zIndex: 1, overflow: "hidden" }}>
        {/* Pure CSS ambient — amber bloom + faint starfield, no video. */}
        <div aria-hidden style={{ position: "absolute", inset: 0, pointerEvents: "none", background: [
          "radial-gradient(circle at 30% 20%, rgba(196,130,58,0.22), transparent 55%)",
          "radial-gradient(circle at 80% 80%, rgba(196,130,58,0.10), transparent 50%)",
          "radial-gradient(1px 1px at 60px 40px, rgba(255,220,170,0.35), transparent 60%)",
          "radial-gradient(1px 1px at 220px 120px, rgba(255,220,170,0.25), transparent 60%)",
          "radial-gradient(1.5px 1.5px at 380px 220px, rgba(255,220,170,0.30), transparent 65%)",
        ].join(", "), backgroundSize: "auto, auto, 480px 320px, 480px 320px, 480px 320px" }} />
        <p style={{ ...eyebrow, color: "rgba(196,130,58,0.9)", marginBottom: 14, position: "relative" }}>One more thing</p>
        <h2 style={{ fontFamily: "'Instrument Serif', serif", fontStyle: "italic", fontSize: "clamp(36px, 5vw, 56px)", margin: "0 0 12px", position: "relative" }}>Ready to build your circle?</h2>
        <p style={{ color: "rgba(248,244,236,0.82)", marginBottom: 18, position: "relative", maxWidth: 580, marginLeft: "auto", marginRight: "auto", fontSize: 17, lineHeight: 1.6 }}>
          I read every application personally — most hear back inside 48 hours. Come build something the giants can't copy.
        </p>
        <div style={{ marginBottom: 24, position: "relative" }}>
          <EvaVoiceTap clipId="insider_apply" hint="one last thing" />
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "center", position: "relative" }}>
          <Link to={applyHref} style={{ ...primaryBtn, background: gold, color: ink }}>{applyLabelFinal}</Link>
          <Link to="/ambassadors/playbook" style={{ ...primaryBtn, background: "transparent", color: "#F8F4EC", border: "1px solid rgba(248,244,236,0.35)" }}>How to run a Circle</Link>
        </div>
      </section>
    </AmbassadorShell>
  );
}

function Section({ title, eyebrow: e, children }: { title: string; eyebrow: string; children: React.ReactNode }) {
  return (
    <Reveal as="section">
      <section style={{ marginTop: 80, position: "relative", zIndex: 1 }}>
        <p style={eyebrow}>
          <span aria-hidden style={{ display: "inline-block", width: 6, height: 6, borderRadius: 999, background: "#C4823A", boxShadow: "0 0 8px #C4823A", marginRight: 8, verticalAlign: "middle" }} />
          {e}
        </p>
        <h2 style={{ fontFamily: "'Instrument Serif', serif", fontStyle: "italic", fontSize: "clamp(34px, 4.5vw, 56px)", lineHeight: 1.05, margin: "0 0 24px", fontWeight: 400 }}>{title}</h2>
        {children}
      </section>
    </Reveal>
  );
}

function Row({ title, left, right }: { title: string; left: string; right: string }) {
  return (
    <div>
      <div style={{ ...eyebrow, marginBottom: 6 }}>{title}</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16 }}>
        <div style={{ background: "#FBF8F1", padding: 14, borderRadius: 10, fontSize: 14 }}>{left}</div>
        <div style={{ background: "#FBF8F1", padding: 14, borderRadius: 10, fontSize: 14 }}>{right}</div>
      </div>
    </div>
  );
}

function Stat({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div style={{ background: accent ? ink : "#FBF8F1", color: accent ? "#F8F4EC" : ink, padding: 18, borderRadius: 12 }}>
      <div style={{ ...eyebrow, color: accent ? "rgba(248,244,236,0.6)" : muted, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 28, fontFamily: "'Instrument Serif', serif", fontStyle: "italic", lineHeight: 1 }}>{value}</div>
    </div>
  );
}

function Tier({ badge, title, body, featured }: { badge: string; title: string; body: string; featured?: boolean }) {
  return (
    <div style={{ ...card, borderColor: featured ? ink : line, position: "relative" }}>
      <div style={{ display: "inline-block", background: featured ? ink : gold, color: "#F8F4EC", padding: "4px 10px", borderRadius: 999, fontSize: 12, fontWeight: 700, letterSpacing: "0.04em", marginBottom: 10 }}>{badge} REFERRALS</div>
      <h3 style={{ fontSize: 22, margin: "0 0 8px", fontFamily: "'Instrument Serif', serif", fontStyle: "italic", fontWeight: 400 }}>{title}</h3>
      <p style={{ color: muted, margin: 0 }}>{body}</p>
    </div>
  );
}

function FoundingRow({ rank, reward, featured }: { rank: string; reward: string; featured?: boolean }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 14,
      padding: "12px 14px",
      borderRadius: 10,
      background: featured ? "rgba(196,130,58,0.14)" : "rgba(255,255,255,0.6)",
      border: `1px solid ${featured ? "rgba(196,130,58,0.45)" : "rgba(196,130,58,0.20)"}`,
    }}>
      <span aria-hidden style={{
        flexShrink: 0,
        width: 28, height: 28, borderRadius: 999,
        background: featured ? "#C4823A" : "rgba(196,130,58,0.20)",
        color: featured ? "#F8F4EC" : "#8a5a22",
        display: "inline-flex", alignItems: "center", justifyContent: "center",
        fontSize: 14, fontWeight: 700,
      }}>★</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: ink, letterSpacing: "0.01em" }}>{rank}</div>
        <div style={{ fontSize: 13, color: muted, marginTop: 2, fontStyle: "italic" }}>{reward}</div>
      </div>
    </div>
  );
}

const grid3: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
  gap: 16,
};

const FAQS = [
  { q: "What do you mean by 'Circle'?", a: "Your Inner Circle is the people you brought in. They're not just stats on your dashboard — they're a real room. As you grow it, you unlock recurring group sessions where they meet each other, share what's working, and the founder drops in. The Circle is the community we're building together. The commissions are how I make sure the work gets paid. We wrote a full playbook on how to run one — read it at /ambassadors/playbook." },
  { q: "How do I actually run a Circle?", a: "We wrote a free, founder-voiced playbook that walks you through it: 5–15 owners, 60 minutes, weekly or biweekly. You'll find three meeting templates (Hot Seat, Build Night, Office Hours), the house rules, the research-backed structure, and the tools we recommend. Read the full playbook at /ambassadors/playbook — you can run your first one this week." },
  { q: "Do I need to know how to run a mastermind?", a: "No. We wrote a free playbook with three formats, a recommended cadence, and the founder's house rules. Pick a template, send the invite, run your first one in 60 minutes — link at /ambassadors/playbook." },
  { q: "When do I get paid?", a: "On the 1st of the month, into your bank account via Stripe. Each commission waits out a 30-day hold after the customer pays, then goes out in the next run — so you're paid for revenue that stuck. Minimum payout is $50; anything smaller rolls into the next month." },
  { q: "What if a customer refunds or disputes a payment?", a: "A refund, chargeback or payment dispute reverses the matching commission. If your balance goes negative, it rolls into the next month — you're never asked to send money back." },
  { q: "How long do I earn per customer?", a: "Their first three months. You earn 10% of the subscription fees they pay in that time — about $450 on a $1,500/mo plan. After that, the commission stops. No surprises." },
  { q: "Who counts as a new customer?", a: "A business that has never had a paid ElectricSheepHQ or HireEva account, buying through your link or code. I pay on the people you bring in directly — there's no bonus on who they bring in." },
  { q: "Can I refer myself or my own company?", a: "Don't. Self-referrals get the account banned and any pending commissions zeroed. The buyer's email can't match your Insider account email." },
  { q: "How do I get tax forms?", a: "We collect a W-9 (US) or W-8BEN (outside the US) before your first payout. We issue Form 1099 when US reporting rules require it." },
];

// ----- Simulator -----
// Each cohort of `perMonth` referrals is added at the start of every month.
// Each referral pays commission on its first three monthly payments, then
// stops. Assumes Build Velocity 1, paid monthly.
const BUILD_V1_MO = 1500;
const COMMISSION_RATE = 0.10;
const COMMISSION_MONTHS = 3;
const PER_CUSTOMER = Math.round(BUILD_V1_MO * COMMISSION_RATE * COMMISSION_MONTHS); // $450

function commissionForReferralAge(age: number): number {
  // age is 1-indexed: 1 = their first month
  if (age < 1 || age > COMMISSION_MONTHS) return 0;
  return BUILD_V1_MO * COMMISSION_RATE;
}

function simulateCircle({ perMonth, months }: { perMonth: number; months: number }) {
  const monthly: number[] = new Array(months).fill(0);

  // For each month a cohort is added (0..months-1), they pay commission for their first three months.
  for (let cohortStart = 0; cohortStart < months; cohortStart++) {
    for (let age = 1; age <= COMMISSION_MONTHS; age++) {
      const monthIdx = cohortStart + age - 1;
      if (monthIdx >= months) break;
      monthly[monthIdx] += perMonth * commissionForReferralAge(age);
    }
  }

  const peakMonthly = Math.round(Math.max(...monthly, 0));
  const totalPeriod = Math.round(monthly.reduce((a, b) => a + b, 0));

  return {
    monthly: monthly.map((v) => Math.round(v)),
    peakMonthly,
    totalPeriod,
  };
}

// ----- Milestones -----
const MILESTONES: { amount: number; emoji: string; label: string; special?: boolean }[] = [
  { amount: 50,    emoji: "☕", label: "Coffee for the office" },
  { amount: 200,   emoji: "📱", label: "Office phone bill" },
  { amount: 695,   emoji: "✨", label: "Your own Maintain Velocity plan, covered", special: true },
  { amount: 800,   emoji: "🥗", label: "Weekly team lunch" },
  { amount: 2200,  emoji: "🏔️", label: "Quarterly team retreat" },
  { amount: 5000,  emoji: "🏖️", label: "Family vacation fund" },
  { amount: 12000, emoji: "💼", label: "New senior operator" },
];

function Milestone({ milestone, unlocked }: { milestone: typeof MILESTONES[number]; unlocked: boolean }) {
  const special = milestone.special;
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      padding: "8px 12px",
      borderRadius: 8,
      background: unlocked ? (special ? "rgba(196,130,58,0.14)" : "rgba(196,130,58,0.07)") : "transparent",
      border: special ? `1px solid ${unlocked ? "rgba(196,130,58,0.5)" : "rgba(196,130,58,0.25)"}` : "1px solid transparent",
      opacity: unlocked ? 1 : 0.45,
      transition: "all 200ms ease",
    }}>
      <span aria-hidden style={{ fontSize: 18, lineHeight: 1, width: 22, textAlign: "center" }}>{milestone.emoji}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: unlocked ? 600 : 500, color: ink }}>
          ${milestone.amount.toLocaleString()}/mo
          <span style={{ color: muted, fontWeight: 400, marginLeft: 6 }}>· {milestone.label}</span>
        </div>
      </div>
      {unlocked && (
        <span aria-hidden style={{ color: "#C4823A", fontSize: 14, fontWeight: 700 }}>✓</span>
      )}
    </div>
  );
}

function Sparkline({ values }: { values: number[] }) {
  const max = Math.max(...values, 1);
  return (
    <div style={{ display: "flex", alignItems: "flex-end", gap: 3, height: 80, padding: "4px 0", borderBottom: `1px solid ${line}` }}>
      {values.map((v, i) => {
        const h = Math.max(2, (v / max) * 100);
        return (
          <div key={i} style={{
            flex: 1,
            height: `${h}%`,
            background: `linear-gradient(180deg, rgba(196,130,58,0.85) 0%, rgba(196,130,58,0.45) 100%)`,
            borderRadius: "3px 3px 0 0",
            minWidth: 4,
            transition: "height 200ms ease",
          }} title={`Month ${i + 1}: $${Math.round(v).toLocaleString()}`} />
        );
      })}
    </div>
  );
}
